import { useTranslation } from "react-i18next";
import { RotateCcw, Star, X } from "lucide-react";
import { Input as AriaInput, Label, TextField } from "react-aria-components";
import { Button } from "@/components/ui/button";
import { PopoverTitle } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type { ListPage } from "./useDashboardListStore.js";
import { advancedFiltersActive, LIST_STATUS_LABEL_KEY, LIST_STATUSES, type ManualListFilters } from "./manual-list-model.js";

/**
 * The legacy "Weitere Filter" window as a popover panel (ADR-034 D2): facility / creator "contains" fields, the three
 * status pills, the caller switches (created by me · assigned to me · only linked), the 5-star band picker (Ratings
 * page, when the tenant has ratings) and the dashboard-only "show archived" switch (Manuals page). All client-side.
 */
export function ManualListFiltersPanel({
  mode,
  filters,
  onChange,
  onReset,
  onClose,
  archived,
  onArchivedChange,
  ratingsEnabled,
}: {
  mode: ListPage;
  filters: ManualListFilters;
  onChange: (patch: Partial<ManualListFilters>) => void;
  onReset: () => void;
  onClose: () => void;
  archived: boolean;
  onArchivedChange: (archived: boolean) => void;
  ratingsEnabled: boolean;
}) {
  const { t } = useTranslation();
  const field = "border-border bg-background focus-visible:border-primary focus-visible:ring-primary h-8 w-full rounded-md border px-2 text-sm outline-none focus-visible:ring-1";
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <PopoverTitle>{t("dashboard.moreFilters")}</PopoverTitle>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon-sm" aria-label={t("dashboard.resetFilters")} isDisabled={!advancedFiltersActive(filters)} onPress={onReset}>
            <RotateCcw aria-hidden="true" className="size-4" />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={t("dashboard.close")} onPress={onClose}>
            <X aria-hidden="true" className="size-4" />
          </Button>
        </div>
      </div>

      <TextField value={filters.facility} onChange={(facility) => onChange({ facility })} className="flex flex-col gap-1">
        <Label className="text-muted-foreground text-xs">{t("dashboard.facility")}</Label>
        <AriaInput placeholder={t("dashboard.facilityNameContains")} className={field} />
      </TextField>
      <TextField value={filters.creator} onChange={(creator) => onChange({ creator })} className="flex flex-col gap-1">
        <Label className="text-muted-foreground text-xs">{t("dashboard.creator")}</Label>
        <AriaInput placeholder={t("dashboard.creatorContains")} className={field} />
      </TextField>

      <div className="flex flex-col gap-1">
        <span className="text-muted-foreground text-xs">{t("manualFilter.status")}</span>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t("manualFilter.status")}>
          {LIST_STATUSES.map((s) => {
            const on = filters.statuses.includes(s);
            return (
              <button
                key={s}
                type="button"
                aria-pressed={on}
                onClick={() => onChange({ statuses: on ? filters.statuses.filter((x) => x !== s) : [...filters.statuses, s] })}
                className={cn(
                  "focus-visible:ring-ring/50 cursor-pointer rounded-full border-0 px-3 py-1 text-xs font-medium outline-none focus-visible:ring-2",
                  on ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground",
                )}
              >
                {t(LIST_STATUS_LABEL_KEY[s])}
              </button>
            );
          })}
        </div>
      </div>

      {mode === "ratings" && ratingsEnabled ? (
        <div className="flex flex-col gap-1">
          <span className="text-muted-foreground text-xs">{t("manualFilter.rating")}</span>
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label={t("manualFilter.rating")}>
            {([1, 2, 3, 4, 5] as const).map((r) => (
              <button
                key={r}
                type="button"
                aria-label={t("dashboard.starsCount", { count: r })}
                aria-pressed={filters.rating === r}
                onClick={() => onChange({ rating: filters.rating === r ? 0 : r })}
                className={cn(
                  "focus-visible:ring-ring/50 cursor-pointer rounded-full border-0 p-1 outline-none focus-visible:ring-2",
                  filters.rating !== 0 && r <= filters.rating ? "text-attention" : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Star aria-hidden="true" className={cn("size-4", filters.rating !== 0 && r <= filters.rating && "fill-current")} />
              </button>
            ))}
            <span className="text-muted-foreground ml-1 text-xs">{filters.rating === 0 ? t("dashboard.anyRating") : t("dashboard.starsFilter", { count: filters.rating })}</span>
          </div>
        </div>
      ) : null}

      <SwitchRow label={t("manualSelection.filter.createdByMe")}>
        <Switch aria-label={t("manualSelection.filter.createdByMe")} isSelected={filters.createdByMe} onChange={(createdByMe) => onChange({ createdByMe })} />
      </SwitchRow>
      <SwitchRow label={t("manualSelection.filter.assignedToMe")}>
        <Switch aria-label={t("manualSelection.filter.assignedToMe")} isSelected={filters.assignedToMe} onChange={(assignedToMe) => onChange({ assignedToMe })} />
      </SwitchRow>
      <SwitchRow label={t("dashboard.onlyLinked")}>
        <Switch aria-label={t("dashboard.onlyLinked")} isSelected={filters.onlyLinked} onChange={(onlyLinked) => onChange({ onlyLinked })} />
      </SwitchRow>
      {mode === "manuals" ? (
        <SwitchRow label={t("facilityManagement.showArchivedSwitch")}>
          <Switch aria-label={t("facilityManagement.showArchivedSwitch")} isSelected={archived} onChange={onArchivedChange} />
        </SwitchRow>
      ) : null}
    </div>
  );
}

function SwitchRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span>{label}</span>
      {children}
    </div>
  );
}
